import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/user.ts";
import { useWebStyleStore } from "@/stores/webStyle";
import { useSidebarStyleStore } from "@/stores/sidebarStyle";
import { makeTabRouter } from "@/config/tab-config";
import { makeViewRouter, getTabSubMenus, resolveTabLanding } from "@/config/view-config";
import { preloadEditorExtensions } from "@/util/md-extensions";
import {
  applySeo,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  isNoindexRoute,
  ROUTE_DESCRIPTIONS,
} from "@/util/seo";

import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import View403 from "@/views/View403.vue";
// 路由级代码分割：404 页面使用动态导入
const View404 = () => import("@/views/View404.vue");

const routers = [] as RouteRecordRaw[];

// 含 md-editor-v3 的路由：进入这些页面时后台预热 mermaid/prettier/cropper 等重依赖，
// 让工具栏动作（格式化、图片裁剪）与后续挂载的组件无需再等待。
//
// 这里只是性能优化，不是正确性开关：
// 公式渲染的正确性由 main.ts 中 MdPreview/MdEditor 异步加载器 await ensureKatex() 保证，
// 因此本名单即使遗漏路由，也只会变慢，不会出现公式加载不出来的问题。
const EDITOR_ROUTES = new Set([
  // MdEditor 直接渲染的页面
  "manage-problem",
  "manage-problem-create",
  "manage-problem-daily",
  "manage-problem-daily-create",
  "manage-web",
  "collection-edit",
  "collection-create",
  "contest-edit",
  "contest-create",
  "discuss-edit",
  "discuss-create",
  "manage-bot-game",
  // 含内联回复编辑器的详情页（编辑器由 v-if 控制，预热可避免点击后长时间等待）
  "discuss-detail",
  "contest-discuss-detail",
  "bot-about",
  "system-about",
]);

makeTabRouter(routers);
makeViewRouter(routers);

// 错误页：meta.title 决定 <title>（App.vue 会拼上站点名），缺了它标题会停留在
// 上一页——从题目页点进一个坏地址，标题还写着「题库」，这本身就是个 bug。
// 文案里带错误码，让人和爬虫都能一眼看出这是错误页。
routers.push({
  path: "/403",
  name: "403",
  component: View403,
  meta: { title: "403 没有访问权限" },
});

routers.push({
  path: "/:pathMatch(.*)*",
  name: "404",
  component: View404,
  meta: { title: "404 页面不存在" },
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routers,
});

router.beforeEach((to, _, next) => {
  if (to.name == null || !router.hasRoute(to.name)) {
    next({ name: "home" });
    return;
  }

  const toMeta = to.matched[to.matched.length - 1].meta;
  if (toMeta.redirect) {
    // 不直接跟 redirect 走：它只是「权限齐全的人」的默认落点。用户没有那个子页权限时
    // （只有 i-manage-permission 的管理员 + 「管理」默认落点 manage-web），跟过去就是 403
    // ——入口点得开、进去却报错。resolveTabLanding 会在默认落点不可用时改取该 tab 下
    // 第一个有权限的子页；一个都没有时返回 undefined，照旧走 403（那说明本来就不该有入口）。
    const landing = resolveTabLanding(
      toMeta.tab as string,
      toMeta.redirect as string,
      useUserStore()
    );
    next({ name: landing ?? (toMeta.redirect as string) });
    return;
  }

  if (toMeta.needLogin) {
    const userStore = useUserStore();
    if (!userStore.isLogin()) {
      next({ name: "login", query: { redirect_uri: to.fullPath } });
      return;
    }
  }

  // tab 与 view 两级权限都要满足。
  // 此前只校验 tabAuths，导致 view-config 里声明的 view.auths（例如 manage-problem 的
  // i-manage-problem）实际上从未生效——那些页面只靠后端接口拒绝兜底。
  const tabAuths = toMeta.tabAuths as string[] | undefined;
  const viewAuths = toMeta.auths as string[] | undefined;
  const auths = [...(tabAuths ?? []), ...(viewAuths ?? [])];
  if (auths.length > 0) {
    const userStore = useUserStore();
    if (!userStore.hasAllAuths(auths)) {
      next({ name: "403", query: { target: to.fullPath } });
      return;
    }
  }

  next();
});

router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  // 同一页面内切换地址（声明了 viewKey 的多路由界面，如 AI 教练主界面的
  // /ai/conversation 与 /ai/conversation/:id）：标题与侧边栏都没变，
  // 不必重复设置，也不该把窗口滚回顶部
  const viewKey = to.meta.viewKey as string | undefined;
  if (to.name === from.name || (viewKey && viewKey === from.meta.viewKey)) {
    return;
  }

  // 进入需要 markdown 编辑器的页面时，后台预热 md-editor-v3 重依赖
  if (EDITOR_ROUTES.has(to.name as string)) {
    preloadEditorExtensions();
  }

  const sidebarStyleStore = useSidebarStyleStore();
  const webStyleStore = useWebStyleStore();

  let toMeta = null;
  if (to.matched.length > 0) {
    toMeta = to.matched[to.matched.length - 1].meta;
  }
  // let fromMeta = null;
  // if (from.matched.length > 0) {
  //   fromMeta = from.matched[from.matched.length - 1].meta;
  // }

  if (toMeta?.title) {
    webStyleStore.setRouteTitle(toMeta?.title as string);
    webStyleStore.setTitle(toMeta?.title as string);
  }

  // SEO：<title> 由 webStyleStore -> App.vue 那条链路维护（详情页会用自己的标题覆盖），
  // 这里负责页面级 description / canonical / robots，并清掉上一页的结构化数据——
  // 不清的话，从题目页跳到列表页会把 LearningResource 一起带过去。
  // 详情页（题目/讨论）在数据到达后会再调用 seo.ts 的专用方法，用真实内容覆盖这里的兜底值。
  const routeName = to.name as string | undefined;
  applySeo({
    description: ROUTE_DESCRIPTIONS[routeName ?? ""] ?? DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
    canonical: to.path,
    type: "website",
    noindex: isNoindexRoute(routeName, toMeta?.needLogin as boolean | undefined),
    jsonLd: [],
  });

  if (toMeta?.showSidebar) {
    sidebarStyleStore.setSidebarShow(true);
    // 路由 meta 里的 sidebar/tab 由本项目自己写入（见 parseViewRouter），运行时一定是字符串
    const sidebarName = toMeta?.sidebar as string;
    // 传入 store 以便按 view 级权限过滤子菜单：不过滤的话，无权项仍会显示，
    // 用户点进去只会撞上本文件 beforeEach 里的 403 跳转。
    const menus = getTabSubMenus(sidebarName, toMeta?.tab as string, useUserStore());
    sidebarStyleStore.setMenuList(menus);
    sidebarStyleStore.setMenuActiveName(sidebarName);
  } else {
    sidebarStyleStore.setSidebarShow(false);
  }
  window.scrollTo(0, 0);
});

export default router;
